import React, { useState } from 'react';
import { Typography } from '@/lib/typography';

export default function FounderEditForm({ founder, onSave, onCancel, saving }) {
  const [formData, setFormData] = useState({
    first_name: founder.first_name || '',
    last_name: founder.last_name || '',
    email: founder.email || '',
    secondary_email: founder.secondary_email || '',
    linkedin_url: founder.linkedin_url || '',
    title: founder.title || '',
    startup_name: founder.startup_name || '',
    contact_status: founder.contact_status || 'Not Contacted',
    email_status: founder.email_status || 'Valid',
    outreach_channel: founder.outreach_channel || '',
    audience_type: founder.audience_type || '',
    in_mailchimp_audience: founder.in_mailchimp_audience || false,
    notes: founder.notes || '',
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.startup_name.trim()) newErrors.startup_name = 'Startup name is required';
    if (formData.secondary_email && !/\S+@\S+\.\S+/.test(formData.secondary_email)) {
      newErrors.secondary_email = 'Invalid email format';
    }
    if (formData.linkedin_url && !formData.linkedin_url.startsWith('http')) {
      newErrors.linkedin_url = 'URL must start with http:// or https://';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const contactStatuses = ['Not Contacted', 'Contacted', 'Replied', 'Meeting Scheduled', 'Meeting Completed'];
  const emailStatuses = ['Valid', 'Invalid', 'Bounced', 'Unsubscribed', 'Added'];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column - Personal Info */}
        <div className="space-y-4">
          <h3 className={Typography.h3}>Personal Information</h3>

          <div>
            <label className="block text-slate-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.first_name && <p className="text-red-500 mt-1">{errors.first_name}</p>}
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Last Name</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.email && <p className="text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Secondary Email</label>
            <input
              type="email"
              value={formData.secondary_email}
              onChange={(e) => handleChange('secondary_email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.secondary_email ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.secondary_email && <p className="text-red-500 mt-1">{errors.secondary_email}</p>}
          </div>

          <div>
            <label className="block text-slate-700 mb-1">LinkedIn URL</label>
            <input
              type="url"
              value={formData.linkedin_url}
              onChange={(e) => handleChange('linkedin_url', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.linkedin_url ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.linkedin_url && <p className="text-red-500 mt-1">{errors.linkedin_url}</p>}
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-slate-700 mb-1">
              Startup Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.startup_name}
              onChange={(e) => handleChange('startup_name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg ${errors.startup_name ? 'border-red-500' : 'border-gray-300'}`}
            />
            {errors.startup_name && <p className="text-red-500 mt-1">{errors.startup_name}</p>}
          </div>
        </div>

        {/* Right Column - CRM Metadata */}
        <div className="space-y-4">
          <h3 className={Typography.h3}>CRM Metadata</h3>

          <div>
            <label className="block text-slate-700 mb-1">Contact Status</label>
            <select
              value={formData.contact_status}
              onChange={(e) => handleChange('contact_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {contactStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Email Status</label>
            <select
              value={formData.email_status}
              onChange={(e) => handleChange('email_status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {emailStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Outreach Channel</label>
            <input
              type="text"
              value={formData.outreach_channel}
              onChange={(e) => handleChange('outreach_channel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Audience Type</label>
            <input
              type="text"
              value={formData.audience_type}
              onChange={(e) => handleChange('audience_type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.in_mailchimp_audience}
              onChange={(e) => handleChange('in_mailchimp_audience', e.target.checked)}
              className="h-4 w-4 text-brand-600 border-gray-300 rounded"
            />
            <label className="ml-2 block text-slate-700">
              In Mailchimp Audience
            </label>
          </div>

          <div>
            <label className="block text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-lg text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 bg-brand-900 text-white rounded-lg hover:bg-brand-950 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}
